
import Vue from 'vue';
import type { PropOptions } from 'vue';
import { mapActions, mapState } from 'vuex';
import { maxLength, required } from 'vuelidate/lib/validators';
import type { Validation } from 'vuelidate';
import { validationMixin } from 'vuelidate';
import type { PublicPortfolioConfiguration } from '@/types/graphql';
import PublicPortfolioModal from '~/components/partials/public-portfolio/PublicPortfolioModal.vue';
import BravadoCallout from '~/components/common/BravadoCallout/BravadoCallout.vue';
import BravadoToggle from '~/components/common/BravadoToggle/BravadoToggle.vue';
import BravadoButtonV3 from '~/components/common/BravadoButtonV3/BravadoButtonV3.vue';
import PlusIcon from '~/assets/svg/plus.vue.svg';
import PublicPortfolioEditAward from '~/components/partials/public-portfolio/PublicPortfolioAwards/PublicPortfolioEditAward.vue';
import { uid } from '~/components/utils/core/uid';
import { loadingState } from '~/components/common/constants';
import { cloneDeep } from '~/components/utils/core/object';

type Award = {
  id?: string;
  year?: number;
  name?: string;
  _isNew?: boolean;
  _destroy?: boolean;
};

interface IData {
  loadingState: string;
  form: {
    awards: Award[];
  };
}

export default Vue.extend({
  name: 'PublicPortfolioAwardsModal',
  components: {
    PublicPortfolioEditAward,
    PublicPortfolioModal,
    BravadoCallout,
    BravadoToggle,
    BravadoButtonV3,
    PlusIcon,
  },
  mixins: [validationMixin],
  props: {
    configuration: {
      type: Object,
      required: true,
    } as PropOptions<PublicPortfolioConfiguration>,
  },
  data(): IData {
    return {
      loadingState: loadingState.IDLE,
      form: {
        awards: [],
      },
    };
  },
  computed: {
    ...mapState('portfolio', ['awards']),
    isSubmitButtonDisabled(): boolean {
      return this.loadingState !== loadingState.IDLE || !this.form.awards.length;
    },
  },
  validations: {
    form: {
      awards: {
        $each: {
          year: { required },
          name: { required, maxLength: maxLength(80) },
        },
      },
    },
  },
  mounted() {
    this.form.awards = cloneDeep(this.awards);
  },
  methods: {
    ...mapActions('portfolio', ['saveAwards']),
    addEmptyAward(): void {
      this.form.awards.unshift({
        id: uid(),
        year: new Date().getFullYear(),
        name: '',
        _isNew: true,
      });
    },
    removeAward(index: number): void {
      if (this.form.awards[index]._isNew) {
        this.form.awards.splice(index, 1);
      } else {
        this.$set(this.form.awards[index], '_destroy', true);
      }
    },
    onVisibilityChange(value: boolean): void {
      this.$emit('upsert', {
        configurationAttrs: {
          sectionAwardsIsPublic: value,
        },
      });
    },
    onCancel(): void {
      this.form.awards = cloneDeep(this.awards);
      this.$modal.hide(this.$constants.dynamicModals.PUBLIC_PORTFOLIO_AWARDS_MODAL);
    },
    async onSaveAwards(): Promise<void> {
      this.$v.$touch();
      if ((this.$v.form.awards as Validation).$invalid) return;

      this.loadingState = loadingState.SUBMITTING;
      const payload = this.form.awards.map(({ id, year, name, _isNew, _destroy }) => {
        let award: Award = { id, name, year };
        if (_isNew) award = { name, year };
        else if (_destroy) award = { id, _destroy };
        return award;
      });
      try {
        await this.saveAwards(payload);
        this.form.awards = cloneDeep(this.awards);
        this.$notice.success('Awards successfully saved');
        this.$modal.hide(this.$constants.dynamicModals.PUBLIC_PORTFOLIO_AWARDS_MODAL);
      } catch (e) {
        this.$notice.error(e);
        this.$sentryUtils.error(e);
      } finally {
        this.loadingState = loadingState.IDLE;
      }
    },
  },
});
